import { TLeadMassiveEdit } from './types'
import api from '@/core/config/axios'
import { DataWithPagination, TFilters } from '@/core/types'
import {
  createCatalogEndpoint,
  createCrudEndpoints,
  createExportEndpoint,
  createNestedCrudEndpoints,
  createPrintEndpoint,
} from '@/core/utils'
import { toValue } from 'vue'
import {
  TAdvisors,
  TAppointment,
  TAppointments,
  TAppointmentsCatalog,
  TCampaigns,
  TCatalogs,
  TComment,
  TLead,
  TOrigins,
  TProject,
  TQuote,
  TStatuses,
} from '.'
import { TLot, TLots } from '../projects'

export type TLeads = DataWithPagination<TLead>
export type TComments = TComment[]
export type TQuotations = DataWithPagination<TQuote>

export const serviceConfig = {
  model: 'leads',
  version: 'v1',
  comments: {
    model: 'comments',
    version: 'v1',
  },
  projects: {
    model: 'projects',
    version: 'v1',
  },
  appointmentTypes: {
    model: 'appointment-types',
    version: 'v1',
  },
  appointments: {
    model: 'appointments',
    version: 'v1',
  },
  advisors: {
    model: 'advisors',
    version: 'v1',
  },
  origins: {
    model: 'origins',
    version: 'v1',
  },
  campaigns: {
    model: 'campaigns',
    version: 'v1',
  },
  statuses: {
    model: 'lead-status',
    version: 'v1',
  },
  quotations: {
    model: 'quotations',
    version: 'v1',
  },
  lots: {
    model: 'lots',
    version: 'v1',
  },
  bulk: {
    model: 'bulk',
    version: 'v1',
  },
  lostReasons: {
    model: 'lead/lost-reasons',
    version: 'v1',
  },
  socialNetworks: {
    model: 'social-networks',
    version: 'v1',
  },
  referrals: {
    model: 'referrals',
    version: 'v1',
  },
}

const {
  comments,
  projects,
  appointmentTypes,
  appointments,
  advisors,
  origins,
  campaigns,
  statuses,
  quotations,
  lots,
  bulk,
  lostReasons,
  socialNetworks,
  referrals,
} = serviceConfig

const moduleServiceHandlers = createCrudEndpoints<TLeads, TLead>(
  serviceConfig.model,
  serviceConfig.version,
)

const commentsServiceHandlers = createNestedCrudEndpoints<TComments, TComment>(
  serviceConfig.model,
  comments.model,
  comments.version,
)

const advisorsServiceHandler = createCatalogEndpoint<TAdvisors>(
  advisors.model,
  advisors.version,
)

const originsServiceHandler = createCatalogEndpoint<TOrigins>(
  origins.model,
  origins.version,
)

const campaignsServiceHandler = createCatalogEndpoint<TCampaigns>(
  campaigns.model,
  campaigns.version,
)

const statusesServiceHandler = createCatalogEndpoint<TStatuses>(
  statuses.model,
  statuses.version,
)

const lostReasonsServiceHandler = createCatalogEndpoint<TStatuses>(
  lostReasons.model,
  lostReasons.version,
)

const projectsServiceHandler = createCatalogEndpoint<TProject[]>(
  projects.model,
  projects.version,
)

const leadsLotsServiceHandler = createCatalogEndpoint<TLots[]>(
  lots.model,
  lots.version,
)

const appointmentTypesServiceHandler =
  createCatalogEndpoint<TAppointmentsCatalog>(
    appointmentTypes.model,
    appointmentTypes.version,
  )

const appointmentsServiceHandler = createCrudEndpoints<
  TAppointments,
  TAppointment
>(serviceConfig.appointments.model, serviceConfig.appointments.version)

const categoriesCatalog = createCatalogEndpoint<TCatalogs>(
  '',
  serviceConfig.version,
)

const lotsServiceHandler = createCatalogEndpoint<TLots>(
  lots.model,
  lots.version,
)

const socialNetworksServiceHandler = createCatalogEndpoint<TCatalogs>(
  socialNetworks.model,
  socialNetworks.version,
)

const projectsLotsService = createNestedCrudEndpoints<TLots, TLot>(
  projects.model,
  lots.model,
  lots.version,
)

const referralsServiceHandler = createCatalogEndpoint<TCatalogs>(
  referrals.model,
  referrals.version,
)

const catalogServiceHandlers = {
  statusesServiceHandler,
  campaignsServiceHandler,
  originsServiceHandler,
  advisorsServiceHandler,
  projectsServiceHandler,
  leadsLotsServiceHandler,
  appointmentTypesServiceHandler,
  appointmentsServiceHandler,
  categoriesCatalog,
  lotsServiceHandler,
  projectsLotsService,
  lostReasonsServiceHandler,
  socialNetworksServiceHandler,
  referralsServiceHandler,
}

const appointmentServiceHandlers = {
  loadRecords: async (params: TFilters): Promise<TAppointment[]> =>
    api
      .get(
        `${appointments.version}/${appointments.model}?${toValue(
          params?.dynamicFilters,
        )}`,
      )
      .then((response) => response.data),
}

const leadMassiveEditHandlers = {
  massiveEditRecords: async (
    params: TFilters,
    payload: TLeadMassiveEdit,
  ): Promise<void> => {
    const urlSearchParams = [
      `page=${toValue(params.page ?? 1)}`,
      toValue(params.dynamicFilters ?? ''),
    ]
      .filter(Boolean)
      .join('&')
    return api
      .post(
        `${serviceConfig.version}/${serviceConfig.model}/${bulk.model}?${urlSearchParams}`,
        payload,
      )
      .then((response) => response.data)
  },
}

const validatePhoneService = {
  validatePhone: async (payload: { phone: string }): Promise<boolean> => {
    return api
      .post(
        `${serviceConfig.version}/${serviceConfig.model}/validate-phone`,
        payload,
      )
      .then((response) => response.data)
  },
}

const quotationsServiceHandlers = createNestedCrudEndpoints<
  TQuotations,
  TQuote
>(serviceConfig.model, quotations.model, quotations.version)

const quotesActionsService = createCrudEndpoints<TQuotations, TQuote>(
  quotations.model,
  quotations.version,
)
createCrudEndpoints
const amortizationScheduleService = createPrintEndpoint(
  quotations.model,
  quotations.version,
)
const moduleExportHandler = createExportEndpoint(serviceConfig.model)

export {
  validatePhoneService,
  amortizationScheduleService,
  appointmentServiceHandlers,
  catalogServiceHandlers,
  commentsServiceHandlers,
  moduleServiceHandlers,
  quotationsServiceHandlers,
  quotesActionsService,
  leadMassiveEditHandlers,
  moduleExportHandler,
}
